import { useDraggable } from "@dnd-kit/core";
import React, { useMemo } from "react";
import { useWorkspaceDetailDragPayload } from "./useWorkspaceDetailDragPayload";

export type DragItemKind = "canvas" | "folder" | "file" | "chat";

export interface SelectableItemProps {
  id: string;
  kind: DragItemKind;
  selectedIds: Set<string>;
  selectOnly: (id: string) => void;
  children: React.ReactNode;
  /** Whether the item can be dragged. Defaults to true. Surfaces whose
   *  drop context can't resolve the item into a drag payload (e.g. chats
   *  on pages without an `inboxItemMap`) pass `false` — selection,
   *  marquee hit-testing, and clearIfOutside still work, but dnd-kit
   *  never starts a drag. */
  draggable?: boolean;
}

export function SelectableItem({
  id,
  selectedIds,
  selectOnly,
  children,
  draggable = true,
}: SelectableItemProps) {
  const selectionSnapshot = useMemo(
    () => (selectedIds.has(id) ? Array.from(selectedIds) : [id]),
    [id, selectedIds],
  );
  const dragData = useWorkspaceDetailDragPayload(selectionSnapshot);

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    disabled: !draggable,
    data: dragData,
  });

  /** Keep the source card in place while dragging; the DragOverlay pill is
   *  the sole moving element so cards and rows feel identical. */
  const style = isDragging ? { opacity: 0.4 } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-workspace-item
      data-selection-id={id}
      /** `select-none` kills native text selection inside the card so
       *  a drag that starts on a title/meta line doesn't also highlight
       *  text and fight the dnd-kit drag image. The marquee rect also
       *  relies on `[data-selection-id]` to hit-test items. */
      className="outline-none focus:outline-none focus-visible:outline-none select-none"
      {...listeners}
      {...attributes}
      onPointerDown={(e) => {
        /** Only auto-select on mousedown for unmodified clicks so that
         *  drag from an unselected item works; modifier-clicks fall through
         *  to the click handler which fully controls the selection. */
        const hasModifier = e.shiftKey || e.metaKey || e.ctrlKey || e.altKey;
        if (!hasModifier && !selectedIds.has(id)) {
          selectOnly(id);
        }
        listeners?.onPointerDown?.(e);
      }}
    >
      {children}
    </div>
  );
}
